/**
 * Swiss market schema.org utilities for Future Level
 * Generates rich structured data for services, locations, and organization
 */

import { SwissLanguage, getCityDisplayName, getCantonForCity } from './swissI18n';

interface OrganizationSchemaProps {
  language: SwissLanguage;
}

interface ServiceSchemaProps {
  serviceName: string;
  serviceSlug: string;
  city?: string;
  language: SwissLanguage;
  priceRange?: string;
  description?: string;
  url: string;
}

interface FAQSchemaProps {
  questions: Array<{
    question: string;
    answer: string;
  }>;
  language: SwissLanguage;
}

/**
 * Generate Organization + ProfessionalService schema for Future Level
 */
export const generateOrganizationSchema = ({ language }: OrganizationSchemaProps) => {
  const baseUrl = 'https://www.future-level.ch';
  
  const organizationNames = {
    de: 'Future Level - KI-Videoagentur Schweiz',
    fr: 'Future Level - Agence Vidéo IA Suisse',
    it: 'Future Level - Agenzia Video AI Svizzera',
    en: 'Future Level - AI Video Agency Switzerland'
  };

  const descriptions = {
    de: 'Führende KI-Videoagentur der Schweiz. Spezialisiert auf AI-Videos, Webdesign, SEO und Marketing-Automation für Unternehmen in Zürich, Basel, Bern, Genf und der ganzen Schweiz.',
    fr: 'Agence vidéo IA leader en Suisse. Spécialisée dans les vidéos IA, webdesign, référencement et automatisation marketing pour entreprises à Genève, Lausanne, Zurich et toute la Suisse.',
    it: 'Agenzia video AI leader in Svizzera. Specializzata in video AI, webdesign, SEO e automazione marketing per aziende a Lugano, Zurigo e tutta la Svizzera.',
    en: 'Leading AI video agency in Switzerland. Specialized in AI videos, web design, SEO and marketing automation for businesses in Zurich, Geneva, Basel and all of Switzerland.'
  };

  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "ProfessionalService"],
    "name": organizationNames[language],
    "alternateName": "Future Level",
    "url": `${baseUrl}/${language}`,
    "logo": `${baseUrl}/opengraph-image-p98pqg.png`,
    "description": descriptions[language],
    "foundingDate": "2023",
    "numberOfEmployees": "5-10",
    "areaServed": {
      "@type": "Country",
      "name": "Switzerland",
      "alternateName": "CH"
    },
    "serviceArea": [
      {
        "@type": "City",
        "name": "Zürich",
        "addressRegion": "ZH",
        "addressCountry": "CH"
      },
      {
        "@type": "City", 
        "name": "Genève",
        "addressRegion": "GE",
        "addressCountry": "CH"
      },
      {
        "@type": "City",
        "name": "Basel",
        "addressRegion": "BS", 
        "addressCountry": "CH"
      },
      {
        "@type": "City",
        "name": "Bern",
        "addressRegion": "BE",
        "addressCountry": "CH"
      },
      {
        "@type": "City",
        "name": "Lausanne",
        "addressRegion": "VD",
        "addressCountry": "CH"
      }
    ],
    "knowsAbout": [
      "AI Video Production",
      "Artificial Intelligence Marketing", 
      "Web Design",
      "Search Engine Optimization",
      "Google Business Profile Optimization",
      "Marketing Automation",
      "E-Commerce Solutions",
      "Multilingual SEO",
      "Swiss Digital Marketing"
    ],
    "sameAs": [
      "https://www.linkedin.com/company/future-level-ch",
      "https://www.youtube.com/@future-level-ch"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+41-XX-XXX-XX-XX",
      "contactType": "customer service",
      "availableLanguage": ["de-CH", "fr-CH", "it-CH", "en-CH"]
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "CH",
      "addressRegion": "Switzerland"
    }
  };
};

/**
 * Generate Service schema for specific service + optional city
 */
export const generateServiceSchema = ({
  serviceName,
  serviceSlug,
  city,
  language,
  priceRange = "CHF 1000-10000",
  description,
  url
}: ServiceSchemaProps) => {
  const cityDisplayName = city ? getCityDisplayName(language, city) : null;
  const canton = cityDisplayName ? getCantonForCity(cityDisplayName) : null;
  
  const serviceNames = {
    'ai-video': {
      de: 'KI-Video Produktion',
      fr: 'Production Vidéo IA',
      it: 'Produzione Video AI',
      en: 'AI Video Production'
    },
    'webdesign': {
      de: 'Webdesign & Website Entwicklung',
      fr: 'Webdesign & Développement Site Web',
      it: 'Webdesign & Sviluppo Siti Web',
      en: 'Web Design & Website Development'
    },
    'seo': {
      de: 'SEO & Suchmaschinenoptimierung',
      fr: 'SEO & Référencement',
      it: 'SEO & Ottimizzazione Motori',
      en: 'SEO & Search Engine Optimization'
    },
    'gbp': {
      de: 'Google Business Profile Optimierung',
      fr: 'Optimisation Google Business Profile',
      it: 'Ottimizzazione Google Business Profile',
      en: 'Google Business Profile Optimization'
    }
  };

  const fullServiceName = serviceNames[serviceSlug]?.[language] || serviceName;
  const locationSuffix = cityDisplayName ? ` ${cityDisplayName}` : ' Schweiz';
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${fullServiceName}${locationSuffix}`,
    "description": description || `Professionelle ${fullServiceName} Dienstleistungen in ${cityDisplayName || 'der Schweiz'}`,
    "provider": {
      "@type": "Organization",
      "name": "Future Level",
      "url": `https://www.future-level.ch/${language}`
    },
    "areaServed": cityDisplayName ? {
      "@type": "City",
      "name": cityDisplayName,
      "addressRegion": canton,
      "addressCountry": "CH"
    } : {
      "@type": "Country",
      "name": "Switzerland",
      "alternateName": "CH"
    },
    "offers": {
      "@type": "Offer",
      "priceRange": priceRange,
      "priceCurrency": "CHF",
      "availability": "https://schema.org/InStock"
    },
    "serviceType": fullServiceName,
    "url": url
  };
};

/**
 * Generate FAQ schema
 */
export const generateFAQSchema = ({ questions, language }: FAQSchemaProps) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(({ question, answer }) => ({
      "@type": "Question",
      "name": question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": answer
      }
    }))
  };
};

/**
 * Generate breadcrumb schema for service pages
 */
export const generateBreadcrumbSchema = (
  language: SwissLanguage,
  service?: string,
  city?: string
) => {
  const baseUrl = 'https://www.future-level.ch';
  
  const homeLabels = {
    de: 'Home',
    fr: 'Accueil', 
    it: 'Home',
    en: 'Home'
  };

  const itemList = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": homeLabels[language],
      "item": `${baseUrl}/${language}`
    }
  ];

  if (service) {
    const serviceDisplayName = service.charAt(0).toUpperCase() + service.slice(1);
    itemList.push({
      "@type": "ListItem",
      "position": 2,
      "name": serviceDisplayName,
      "item": `${baseUrl}/${language}/${service}`
    });

    if (city) {
      const cityDisplayName = getCityDisplayName(language, city);
      itemList.push({
        "@type": "ListItem",
        "position": 3,
        "name": cityDisplayName,
        "item": `${baseUrl}/${language}/${service}/${city}`
      });
    }
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": itemList
  };
};

/**
 * Inject schema into document head
 */
export const injectSchema = (schema: any) => {
  const scriptId = `schema-${Date.now()}`;
  const script = document.createElement('script');
  script.id = scriptId;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
  
  // Clean up old schema tags
  const oldSchemas = document.querySelectorAll('script[type="application/ld+json"]:not(#' + scriptId + ')');
  oldSchemas.forEach(el => el.remove());
};